import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { getCableSpecification, type Specification } from '../api/specification'
import { getDeviceContacts, createDeviceContact, updateDeviceContact, deleteDeviceContact } from '../api/connections'
import type { DeviceContact } from '../types/connections'

interface CableManagementProps {
  projectId: number
  electricalPanelId?: number
}

const CONTACT_COLORS = [
  { value: '', label: 'Не указан', color: 'transparent' },
  { value: 'WH', label: 'WH - Белый', color: '#FFFFFF' },
  { value: 'BK', label: 'BK - Чёрный', color: '#000000' },
  { value: 'BU', label: 'BU - Синий', color: '#0066CC' },
  { value: 'BN', label: 'BN - Коричневый', color: '#8B4513' },
  { value: 'GY', label: 'GY - Серый', color: '#808080' },
  { value: 'GN', label: 'GN - Зеленый', color: '#008000' },
  { value: 'GNYE', label: 'GNYE - Жёлто-зелёный', color: '#FFD700', color2: '#008000' },
  { value: 'YE', label: 'YE - Желтый', color: '#FFD700' },
  { value: 'RD', label: 'RD - Красный', color: '#FF0000' },
  { value: 'OG', label: 'OG - Оранжевый', color: '#FFA500' },
  { value: 'PK', label: 'PK - Розовый', color: '#FFC0CB' },
  { value: 'VT', label: 'VT - Фиолетовый', color: '#8A2BE2' },
  // Цвета витой пары
  { value: 'WO', label: 'WO - Бело-оранжевый', color: '#FFFFFF', color2: '#FFA500' },
  { value: 'WG', label: 'WG - Бело-зелёный', color: '#FFFFFF', color2: '#008000' },
  { value: 'WB', label: 'WB - Бело-синий', color: '#FFFFFF', color2: '#0066CC' },
  { value: 'WN', label: 'WN - Бело-коричневый', color: '#FFFFFF', color2: '#8B4513' }
]




export default function CableManagement({ projectId, electricalPanelId }: CableManagementProps) {
  const params = useParams<{ projectId?: string; panelId?: string }>()
  const currentProjectId = params.projectId ? Number(params.projectId) : projectId
  const currentPanelId = params.panelId ? Number(params.panelId) : electricalPanelId
  
  const [selectedCable, setSelectedCable] = useState<Specification | null>(null)
  const [contacts, setContacts] = useState<DeviceContact[]>([])
  const [showContactManager, setShowContactManager] = useState(false)
  const [showContactEditor, setShowContactEditor] = useState(false)
  const [editingContact, setEditingContact] = useState<DeviceContact | null>(null)
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null)
  const [newWire, setNewWire] = useState<{
    wire_number: number
    color_code: string
  }>({
    wire_number: 1,
    color_code: 'WH'
  })

  // Sorting state
  const [sortField, setSortField] = useState<keyof Specification | 'length_meters' | 'instance_count'>('id')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')


  // Sorting functions
  const handleSort = (field: keyof Specification | 'length_meters' | 'instance_count') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }



  // Sortable header component
  const SortableHeader = ({ field, children }: { field: keyof Specification | 'length_meters' | 'instance_count', children: React.ReactNode }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <span className="flex flex-col">
          <span className={`text-xs ${sortField === field && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}>▲</span>
          <span className={`text-xs ${sortField === field && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}>▼</span>
        </span>
      </div>
    </th>
  )

  // Function to close all modals
  const closeAllModals = () => {
    setShowContactManager(false)
    setShowContactEditor(false)
    setSelectedCable(null)
    setEditingContact(null)
    setMouseDownTarget(null)
    setNewWire({ wire_number: 1, color_code: 'WH' })
  }

  // Function to close only contact editor (keep contact manager open)
  const closeContactEditor = () => {
    setShowContactEditor(false)
    setEditingContact(null)
    setMouseDownTarget(null)
    setNewWire({ wire_number: 1, color_code: 'WH' })
  }

  // Загрузка кабелей проекта
  const { data: cables = [], isLoading: cablesLoading, error: cablesError } = useQuery({
    queryKey: ['cables', projectId, electricalPanelId],
    queryFn: () => getCableSpecification(projectId, electricalPanelId),
    enabled: !!projectId
  })


  // Sorted cables
  const sortedCables = useMemo(() => {
    return [...cables].sort((a, b) => {
      let aValue: any
      let bValue: any

      if (sortField === 'length_meters') {
        aValue = a.length_meters || 0
        bValue = b.length_meters || 0
      } else if (sortField === 'instance_count') {
        aValue = a.instance_count || 0
        bValue = b.instance_count || 0
      } else {
        aValue = a[sortField]
        bValue = b[sortField]
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [cables, sortField, sortDirection])

  // Функция для получения следующего номера жилы
  const getNextWireNumber = () => {
    if (contacts.length === 0) return 1
    
    // Извлекаем номера жил из контактов (убираем -TOP/-BOTTOM)
    const wireNumbers = contacts
      .map(c => parseInt(c.contact_number.toString().split('-')[0]))
      .filter(num => !isNaN(num))
    
    return wireNumbers.length > 0 ? Math.max(...wireNumbers) + 1 : 1
  }

  // Функция для получения жил из контактов (группировка по номеру жилы)
  const getWiresFromContacts = () => {
    const wireMap = new Map()
    
    contacts.forEach(contact => {
      const contactNumber = contact.contact_number.toString()
      const wireNumber = parseInt(contactNumber.split('-')[0])
      
      if (!isNaN(wireNumber)) {
        if (!wireMap.has(wireNumber)) {
          wireMap.set(wireNumber, {
            wire_number: wireNumber,
            color_code: contact.color_code || 'WH',
            topContact: null,
            bottomContact: null
          })
        }
        
        const wire = wireMap.get(wireNumber)
        if (contactNumber.includes('-TOP')) {
          wire.topContact = contact
        } else if (contactNumber.includes('-BOTTOM')) {
          wire.bottomContact = contact
        }
      }
    })
    
    return Array.from(wireMap.values()).sort((a, b) => a.wire_number - b.wire_number)
  }

  // Обработчик ESC для закрытия модальных окон
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showContactEditor) {
          closeContactEditor()
        } else if (showContactManager) {
          closeAllModals()
        }
      }
    }
    
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [showContactEditor, showContactManager])

  // Загрузка контактов выбранного кабеля
  useEffect(() => {
    if (selectedCable) {
      loadCableContacts(selectedCable.id)
    }
  }, [selectedCable])

  const loadCableContacts = async (specificationId: number) => {
    try {
      const cableContacts = await getDeviceContacts(specificationId)
      // Сортируем контакты по номеру жилы (числовая сортировка)
      const sortedContacts = cableContacts.sort((a, b) => {
        const numA = parseInt(String(a.contact_number).split('-')[0])
        const numB = parseInt(String(b.contact_number).split('-')[0])
        return numA - numB
      })
      setContacts(sortedContacts)
    } catch (error) {
      console.error('Failed to load cable contacts:', error)
      setContacts([])
    }
  }


  // Мутация для создания жилы (создает 2 контакта: TOP и BOTTOM)
  const createWireMutation = useMutation({
    mutationFn: async (wireData: { wire_number: number; color_code: string }) => {
      if (!selectedCable) return
      
      // Создаем два контакта для жилы
      const topContact = {
        specification_id: selectedCable.id,
        contact_number: `${wireData.wire_number}-TOP`,
        contact_type: 'L',
        contact_label: `${wireData.wire_number}`,
        color_code: wireData.color_code
      }
      
      const bottomContact = {
        specification_id: selectedCable.id,
        contact_number: `${wireData.wire_number}-BOTTOM`,
        contact_type: 'L',
        contact_label: `${wireData.wire_number}`,
        color_code: wireData.color_code
      }
      
      await createDeviceContact(topContact)
      await createDeviceContact(bottomContact)
    },
    onSuccess: () => {
      if (selectedCable) {
        loadCableContacts(selectedCable.id)
      }
      setShowContactEditor(false)
      setNewWire({ wire_number: getNextWireNumber(), color_code: 'WH' })
    }
  })

  // Мутация для обновления жилы (обновляет оба контакта)
  const updateWireMutation = useMutation({
    mutationFn: async (data: { wireNumber: number; updates: { color_code: string } }) => {
      if (!selectedCable) return
      
      // Найти оба контакта для жилы
      const topContact = contacts.find(c => c.contact_number === `${data.wireNumber}-TOP`)
      const bottomContact = contacts.find(c => c.contact_number === `${data.wireNumber}-BOTTOM`)
      
      if (topContact) {
        await updateDeviceContact(topContact.id, {
          color_code: data.updates.color_code,
          contact_label: `${data.wireNumber}`
        })
      }
      if (bottomContact) {
        await updateDeviceContact(bottomContact.id, {
          color_code: data.updates.color_code,
          contact_label: `${data.wireNumber}`
        })
      }
    },
    onSuccess: () => {
      if (selectedCable) {
        loadCableContacts(selectedCable.id)
      }
      setShowContactEditor(false)
      setEditingContact(null)
      setNewWire({ wire_number: 1, color_code: 'WH' })
    }
  })




  const handleCreateWire = () => {
    if (!selectedCable) return

    if (editingContact) {
      // Обновление существующей жилы
      const contactNumber = editingContact.contact_number.toString()
      const wireNumber = parseInt(contactNumber.split('-')[0])
      
      updateWireMutation.mutate({ 
        wireNumber, 
        updates: { color_code: newWire.color_code }
      })
    } else {
      // Создание новой жилы
      createWireMutation.mutate({
        wire_number: newWire.wire_number,
        color_code: newWire.color_code
      })
    }
  }

  const handleEditWire = (wire: any) => {
    setEditingContact(wire.topContact || wire.bottomContact) // Используем любой из контактов как индикатор редактирования
    setNewWire({
      wire_number: wire.wire_number,
      color_code: wire.color_code
    })
    setShowContactEditor(true)
  }


  // Мутация для удаления жилы (удаляет оба контакта)
  const deleteWireMutation = useMutation({
    mutationFn: async (wireNumber: number) => {
      if (!selectedCable) return
      
      // Найти оба контакта для жилы
      const topContact = contacts.find(c => c.contact_number === `${wireNumber}-TOP`)
      const bottomContact = contacts.find(c => c.contact_number === `${wireNumber}-BOTTOM`)
      
      if (topContact) {
        await deleteDeviceContact(topContact.id)
      }
      if (bottomContact) {
        await deleteDeviceContact(bottomContact.id)
      }
    },
    onSuccess: () => {
      if (selectedCable) {
        loadCableContacts(selectedCable.id)
      }
    }
  })

  const handleDeleteWire = (wire: any) => {
    if (confirm(`Удалить жилу №${wire.wire_number}?`)) {
      deleteWireMutation.mutate(wire.wire_number)
    }
  }

  // Функции быстрого добавления стандартных наборов жил
  const handleQuickAdd3Wire = async () => {
    if (!selectedCable) return
    const colors = ['WH', 'BU', 'GNYE']
    await addWireSet(colors)
  }

  const handleQuickAdd4Wire = async () => {
    if (!selectedCable) return
    const colors = ['WH', 'BK', 'BU', 'GNYE']
    await addWireSet(colors)
  }

  const handleQuickAdd2Wire = async () => {
    if (!selectedCable) return
    const colors = ['WH', 'BU']
    await addWireSet(colors)
  }

  const handleQuickAdd4WireSpecial = async () => {
    if (!selectedCable) return
    const colors = ['WH', 'WH', 'RD', 'BU']
    await addWireSet(colors)
  }

  const handleQuickAdd10Wire = async () => {
    if (!selectedCable) return
    const colors = ['WH', 'BK', 'WH', 'BU', 'WH', 'GNYE', 'WH', 'BN', 'WH', 'OG']
    await addWireSet(colors)
  }

  const handleQuickAdd5Wire = async () => {
    if (!selectedCable) return
    const colors = ['GY', 'BK', 'WH', 'BU', 'GNYE']
    await addWireSet(colors)
  }

  const handleQuickAddTwistedPair = async () => {
    if (!selectedCable) return
    // Стандарт T568B для витой пары
    const colors = ['WO', 'OG', 'WG', 'BU', 'WB', 'GN', 'WN', 'BN']
    await addWireSet(colors)
  }

  // Вспомогательная функция для добавления набора жил
  const addWireSet = async (colors: string[]) => {
    if (!selectedCable) return

    try {
      let nextWireNumber = getNextWireNumber()

      for (let i = 0; i < colors.length; i++) {
        const wireNumber = nextWireNumber + i
        const colorCode = colors[i]

        // Создаем TOP контакт
        await createDeviceContact({
          specification_id: selectedCable.id,
          contact_number: `${wireNumber}-TOP`,
          contact_label: `${wireNumber}`,
          color_code: colorCode
        })

        // Создаем BOTTOM контакт
        await createDeviceContact({
          specification_id: selectedCable.id,
          contact_number: `${wireNumber}-BOTTOM`,
          contact_label: `${wireNumber}`,
          color_code: colorCode
        })
      }

      // Перезагружаем контакты и закрываем редактор
      loadCableContacts(selectedCable.id)
      closeContactEditor()
      
    } catch (error) {
      console.error('Error creating wire set:', error)
    }
  }



  return (
    <>
      <div className="space-y-6">
        {/* Cables Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
            <h2 className="text-xl font-semibold text-gray-900">
              Экземпляры спецификации кабелей
            </h2>
            <Link
              to={`/projects/${currentProjectId}/specification`}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm whitespace-nowrap"
            >
              Вся спецификация проекта
            </Link>
          </div>
        </div>

        {/* Loading state */}
        {cablesLoading && (
          <div className="text-center py-8 text-gray-500">Загрузка спецификаций кабелей...</div>
        )}

        {/* Error state */}
        {cablesError && (
          <div className="text-red-600 bg-red-50 p-4 rounded-md mb-4">
            Ошибка загрузки спецификаций кабелей: {cablesError instanceof Error ? cablesError.message : 'Неизвестная ошибка'}
          </div>
        )}

        {/* Cables table - Desktop */}
        {!cablesLoading && cables.length > 0 && (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader field="id">ID</SortableHeader>
                    <SortableHeader field="manufacturer">Производитель</SortableHeader>
                    <SortableHeader field="name">Название</SortableHeader>
                    <SortableHeader field="length_meters">Кол-во</SortableHeader>
                    <SortableHeader field="instance_count">Количество экземпляров</SortableHeader>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Просмотр
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedCables.map((cable: Specification) => (
                    <tr 
                      key={cable.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        #{cable.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cable.manufacturer || 'Не указан'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={currentPanelId 
                            ? `/projects/${currentProjectId}/panels/${currentPanelId}/cables/${cable.id}`
                            : `/projects/${currentProjectId}/cables/${cable.id}`
                          }
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {cable.name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cable.length_meters ? Math.round(cable.length_meters) : 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cable.instance_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/projects/${currentProjectId}/cable-instances/spec?spec=${cable.id}`}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline"
                          title="Просмотр экземпляров кабеля"
                        >
                          👁️
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {sortedCables.map((cable: Specification) => (
                <div
                  key={cable.id}
                  className="border rounded-lg p-4 border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm">
                        <Link
                          to={currentPanelId 
                            ? `/projects/${currentProjectId}/panels/${currentPanelId}/cables/${cable.id}`
                            : `/projects/${currentProjectId}/cables/${cable.id}`
                          }
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {cable.name}
                        </Link>
                        {' '}
                        <span className="text-gray-400">#{cable.id}</span>
                      </h3>
                      <div className="text-xs text-gray-600 mt-1">
                        <div>{cable.manufacturer || 'Производитель не указан'}</div>
                        <div className="font-medium">Количество экземпляров: {cable.instance_count || 0}</div>
                        <div>Длина: {cable.length_meters ? Math.round(cable.length_meters) : 0} м</div>
                      </div>
                    </div>
                    <div className="ml-2">
                      <Link
                        to={`/projects/${currentProjectId}/cable-instances/spec?spec=${cable.id}`}
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 hover:underline text-sm"
                        title="Просмотр экземпляров кабеля"
                      >
                        👁️
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Empty state */}
        {!cablesLoading && cables.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="mb-4">Спецификации кабелей для этого проекта не найдены.</p>
            <Link
              to={`/projects/${currentProjectId}/specification`}
              className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Перейти к спецификации проекта
            </Link>
          </div>
        )}
      </div>
      </div>

      {/* Contact Management Modal */}
      {showContactManager && selectedCable && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeAllModals()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                🔌 Жилы кабеля "{selectedCable.name}"
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setNewWire({ wire_number: getNextWireNumber(), color_code: 'WH' })
                    setShowContactEditor(true)
                  }}
                  className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
                >
                  ➕ Добавить жилу
                </button>
                <button
                  onClick={closeAllModals}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {getWiresFromContacts().length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="mb-3">Жилы не созданы</p>
                  <button
                    onClick={() => {
                      setNewWire({ wire_number: 1, color_code: 'WH' })
                      setShowContactEditor(true)
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Создать первую жилу
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          №
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Цвет
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Визуально
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Действия
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getWiresFromContacts().map((wire) => (
                        <tr key={wire.wire_number} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {wire.wire_number}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                {CONTACT_COLORS.find(c => c.value === wire.color_code)?.label || 'Неизвестный'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              {(() => {
                                const colorInfo = CONTACT_COLORS.find(c => c.value === wire.color_code);
                                const color = colorInfo?.color || '#ccc';
                                const color2 = colorInfo?.color2;
                                return (
                                  <div 
                                    className="w-6 h-4 rounded border border-gray-400"
                                    style={{
                                      backgroundColor: color2 ? 'transparent' : color,
                                      backgroundImage: wire.color_code === '' 
                                        ? `linear-gradient(45deg, #ccc 25%, transparent 25%), 
                                           linear-gradient(-45deg, #ccc 25%, transparent 25%), 
                                           linear-gradient(45deg, transparent 75%, #ccc 75%), 
                                           linear-gradient(-45deg, transparent 75%, #ccc 75%)`
                                        : color2
                                        ? `linear-gradient(135deg, ${color} 0%, ${color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${color2} 53%, ${color2} 100%)`
                                        : 'none',
                                      backgroundSize: wire.color_code === '' ? '4px 4px' : '100% 100%',
                                      backgroundPosition: wire.color_code === '' ? '0 0, 0 2px, 2px -2px, -2px 0px' : 'center'
                                    }}
                                  />
                                );
                              })()}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <div className="flex justify-end space-x-1">
                              <button
                                onClick={() => handleEditWire(wire)}
                                className="action-icon-btn edit"
                                title="Редактировать"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleDeleteWire(wire)}
                                className="action-icon-btn delete"
                                title="Удалить"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={closeAllModals}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальные окна создания/редактирования кабеля удалены - функционал перенесён на страницу спецификации */}

      {/* Модальное окно создания/редактирования жилы */}
      {showContactEditor && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setMouseDownTarget(e.target)
            }
          }}
          onMouseUp={(e) => {
            if (e.target === e.currentTarget && mouseDownTarget === e.target) {
              closeContactEditor()
            }
            setMouseDownTarget(null)
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingContact ? 'Редактировать жилу' : 'Новая жила'}
              </h3>
              <button
                onClick={closeContactEditor}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Быстрое добавление - только при создании новой жилы */}
              {!editingContact && (
                <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Быстрое добавление наборов жил</h4>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={handleQuickAdd3Wire}
                      disabled={createWireMutation.isPending}
                      className="px-2 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50 leading-tight"
                    >
                      WH, BU, GNYE
                    </button>
                    <button
                      onClick={handleQuickAdd4Wire}
                      disabled={createWireMutation.isPending}
                      className="px-2 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50 leading-tight"
                    >
                      WH, BK, BU, GNYE
                    </button>
                    <button
                      onClick={handleQuickAdd2Wire}
                      disabled={createWireMutation.isPending}
                      className="px-2 py-1.5 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 disabled:opacity-50 leading-tight"
                    >
                      WH, BU
                    </button>
                    <button
                      onClick={handleQuickAdd4WireSpecial}
                      disabled={createWireMutation.isPending}
                      className="px-2 py-1.5 bg-orange-600 text-white rounded text-xs font-medium hover:bg-orange-700 disabled:opacity-50 leading-tight"
                    >
                      WH, WH, RD, BU
                    </button>
                    <button
                      onClick={handleQuickAdd10Wire}
                      disabled={createWireMutation.isPending}
                      className="px-1 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50 leading-tight"
                      style={{ fontSize: '10px' }}
                    >
                      WH, BK, WH, BU, WH, GNYE, WH, BN, WH, OG
                    </button>
                    <button
                      onClick={handleQuickAdd5Wire}
                      disabled={createWireMutation.isPending}
                      className="px-2 py-1.5 bg-indigo-600 text-white rounded text-xs font-medium hover:bg-indigo-700 disabled:opacity-50 leading-tight"
                    >
                      GY, BK, WH, BU, GNYE
                    </button>
                    <button
                      onClick={handleQuickAddTwistedPair}
                      disabled={createWireMutation.isPending}
                      className="px-2 py-1.5 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 disabled:opacity-50 leading-tight"
                    >
                      витуха
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    Быстрое добавление создаст полный набор жил с правильными цветами
                  </div>
                </div>
              )}

              {/* Разделитель */}
              {!editingContact && <div className="border-t border-gray-200 my-4"></div>}

              <div className="flex items-center space-x-3 mb-0">
                <label className="form-label mb-0 flex-shrink-0">Номер жилы</label>
                <input
                  type="number"
                  min="1"
                  value={newWire.wire_number}
                  onChange={(e) => setNewWire(prev => ({
                    ...prev,
                    wire_number: parseInt(e.target.value) || 1
                  }))}
                  className="form-input flex-1"
                  disabled={!!editingContact}
                />
              </div>
              {editingContact && (
                <p className="text-sm text-gray-500 mt-1">Номер жилы нельзя изменить при редактировании</p>
              )}

              <div>
                <label className="form-label">Цвет жилы *</label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {CONTACT_COLORS.filter(color => color.value !== '').map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => {
                        setNewWire(prev => ({
                          ...prev,
                          color_code: color.value
                        }))
                      }}
                      className={`flex items-center space-x-2 p-2 rounded-md border transition-colors ${
                        newWire.color_code === color.value 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div 
                        className="w-4 h-4 rounded border border-gray-400 flex-shrink-0 relative overflow-hidden"
                        style={{
                          backgroundColor: color.color2 ? 'transparent' : color.color,
                          backgroundImage: color.value === '' 
                            ? `linear-gradient(45deg, #ccc 25%, transparent 25%), 
                                linear-gradient(-45deg, #ccc 25%, transparent 25%), 
                                linear-gradient(45deg, transparent 75%, #ccc 75%), 
                                linear-gradient(-45deg, transparent 75%, #ccc 75%)`
                            : color.color2
                            ? `linear-gradient(135deg, ${color.color} 0%, ${color.color} 47%, rgba(0,0,0,0.2) 49%, rgba(0,0,0,0.2) 51%, ${color.color2} 53%, ${color.color2} 100%)`
                            : 'none',
                          backgroundSize: color.value === '' ? '4px 4px' : '100% 100%',
                          backgroundPosition: color.value === '' ? '0 0, 0 2px, 2px -2px, -2px 0px' : 'center'
                        }}
                      />
                      <span className="text-xs text-gray-700">{color.value ? color.label : 'Нет'}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-3 text-sm text-gray-600">
                  Выбрано: <span className="font-medium">{CONTACT_COLORS.find(c => c.value === newWire.color_code)?.label || 'Не указан'}</span>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-md">
                <div className="text-sm text-blue-800">
                  <strong>ℹ️ Информация:</strong>
                  <p className="mt-1">При создании жилы автоматически создаются 2 контакта в базе данных: {newWire.wire_number}-TOP и {newWire.wire_number}-BOTTOM для правильной работы соединений на схеме.</p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={closeContactEditor}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  onClick={handleCreateWire}
                  disabled={createWireMutation.isPending || updateWireMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {editingContact ? 
                    (updateWireMutation.isPending ? 'Сохранение...' : 'Сохранить') :
                    (createWireMutation.isPending ? 'Создание...' : 'Создать')
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
} 